"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react"
import { isAuthenticated, logout, getUserInfo, getJWTToken, setUserLanguage } from "./api"
import { fullLanguageNameToCode, languageCodeToFullName } from "./language-utils"
import { subscribeAuth } from "./auth-broadcast"

// Define the user info type based on the API response
interface UserInfo {
  id?: string
  first_name?: string
  last_name?: string
  email?: string
  role?: string
  subscription?: string | { name: string; [key: string]: any }
  email_verified?: boolean
  credits?: number // legacy column; admin pages only (spec D2)
  bonus_applications?: number // extra applications from referrals (plan 6)
  cv_text_missing?: boolean
  [key: string]: any // For any other fields that might be in the response
}

// Update the AuthContextType interface to include isFirstLogin and onLoginSuccess
interface AuthContextType {
  isLoggedIn: boolean
  userRole: string | null
  userInfo: UserInfo | null
  logout: () => void
  checkAuthStatus: () => boolean
  getUserRole: () => Promise<string | null>
  refreshUserInfo: (force?: boolean) => Promise<void>
  onLoginSuccess: () => void
  isLoading: boolean
  isFirstLogin: boolean
  setIsFirstLogin: (value: boolean) => void
}

// Add isFirstLogin to the context value
const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
  const [userRole, setUserRole] = useState<string | null>(null)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const lastRefreshTimeRef = useRef<number>(0)
  const [currentToken, setCurrentToken] = useState<string | null>(null)
  // Add a new state for tracking first login
  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false)

  // Get current pathname for route detection
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  
  // List of public routes where we don't need to fetch user info
  const publicRoutes = ['/', '/auth', '/pricing', '/companies-landing', '/career-coaches', '/impressum', '/privacy', '/terms']
  const isPublicRoute = publicRoutes.includes(pathname) || pathname.startsWith('/ref/')

  // Function to fetch and update user info with debouncing
  const refreshUserInfo = useCallback(async (force: boolean = false): Promise<void> => {
    // Debounce: only refresh if it's been more than 2 seconds since the last refresh
    const now = Date.now()
    if (!force && now - lastRefreshTimeRef.current < 2000) {
      return
    }

    lastRefreshTimeRef.current = now
    setIsLoading(true)

    try {
      // Check sessionStorage first (for client impersonation), then localStorage (for advisor)
      const token = getJWTToken()
      
      if (!token) {
        setIsLoading(false)
        setIsLoggedIn(false)
        setUserInfo(null)
        setUserRole(null)
        return
      }

      // Skip API call on public routes to avoid unnecessary errors
      if (isPublicRoute && !force) {
        console.log("AuthContext: Skipping getUserInfo on public route:", pathname)
        setIsLoading(false)
        return
      }

      // If token changed since last time, store it
      if (token !== currentToken) {
        setCurrentToken(token)
      }

      const info = await getUserInfo(token)

      if (info && info.length > 0) {
        setUserInfo(info[0])
        setUserRole(info[0].role || null)

        // Store subscription and role in appropriate storage based on session type
        const isImpersonating = typeof window !== "undefined" && (
          sessionStorage.getItem("partner_impersonation") === "true" ||
          sessionStorage.getItem("admin_impersonation") === "true"
        )
        const storage = isImpersonating ? sessionStorage : localStorage
        
        // Store user_role in appropriate storage
        if (info[0].role) {
          storage.setItem("user_role", info[0].role)
          storage.setItem("user_role_cache_time", Date.now().toString())
        }
        
        if (typeof info[0].subscription === "string") {
          const planName = info[0].subscription.charAt(0).toUpperCase() + info[0].subscription.slice(1)
          storage.setItem("userPlan", planName)
        } else if (info[0].subscription && typeof info[0].subscription === "object" && "name" in info[0].subscription) {
          const planName = (info[0].subscription as { name: string }).name.charAt(0).toUpperCase() + (info[0].subscription as { name: string }).name.slice(1)
          storage.setItem("userPlan", planName)
        } else {
          storage.setItem("userPlan", "Free")
        }

        // Handle language synchronization: compare localStorage language with backend pref_lang
        if (info[0].pref_lang) {
          try {
            const backendLanguageCode = fullLanguageNameToCode(info[0].pref_lang)
            const localStorageLanguage = localStorage.getItem("language") as string | null
            
            // If localStorage has a language and it differs from backend, sync localStorage to backend
            if (localStorageLanguage && localStorageLanguage !== backendLanguageCode) {
              try {
                // Extract user_id from JWT token
                const token = getJWTToken()
                if (token) {
                  const tokenParts = token.split(".")
                  if (tokenParts.length > 1) {
                    const payload = JSON.parse(atob(tokenParts[1]))
                    const userId = payload.sub || payload.user_id || payload.id
                    
                    if (userId) {
                      // Convert localStorage language code to full name for backend
                      const fullLanguageName = languageCodeToFullName(localStorageLanguage as any)
                      
                      // Sync localStorage language to backend (fire and forget)
                      setUserLanguage(fullLanguageName, userId).catch(() => {
                        // Don't block language setting if API call fails
                      })
                    }
                  }
                }
              } catch (error) {
                // Continue with setting language even if sync fails
              }
            }
            
            // Set language from localStorage (user's choice before login) if available, otherwise use backend pref_lang
            // Priority: localStorage (user's choice) > backend pref_lang
            const languageToSet = localStorageLanguage || backendLanguageCode
            
            // Set language in localStorage
            localStorage.setItem("language", languageToSet)
            
            // Dispatch custom event to notify i18n context to update
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("language-changed", { detail: { language: languageToSet } }))
            }
          } catch (error) {
            // Error setting language from pref_lang
          }
        }
      }
    } catch (error) {
      // Error fetching user info
    } finally {
      setIsLoading(false)
    }
  }, [currentToken, isPublicRoute, pathname])

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const status = isAuthenticated()
      setIsLoggedIn(status)

      if (status) {
        try {
          await refreshUserInfo(true)
        } catch (error) {
          // Error in auth initialization
        }
      } else {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, []) // Empty dependency array - only run on mount

  // Track token changes for same-tab login/logout.
  // getJWTToken checks sessionStorage first so an impersonated tab tracks
  // the impersonated user's token, not the admin's localStorage token.
  useEffect(() => {
    const token = getJWTToken()
    
    // If token changed (login/logout in same tab)
    if (token !== currentToken) {
      const isInitialSync = currentToken === null && token !== null
      console.log("AuthContext: Token changed from", currentToken, "to", token)
      setCurrentToken(token)

      // Skip the clear-and-refetch on the initial mount sync — the mount
      // effect (checkAuth) already runs refreshUserInfo for that case, and
      // clearing userInfo here causes a visible flicker (e.g. profile photo
      // disappears for ~1s while the refetch is in flight).
      if (!isInitialSync) {
        setUserInfo(null)
        setUserRole(null)
        lastRefreshTimeRef.current = 0

        if (token) {
          // New login - force refresh even on public routes
          setIsLoggedIn(true)
          refreshUserInfo(true)
        } else {
          // Logout
          setIsLoggedIn(false)
          setIsLoading(false)
        }
      }
    }
  }, [currentToken, refreshUserInfo, isLoggedIn]) // Re-run when isLoggedIn changes (after login)

  // Listen for storage events (works for cross-tab changes only)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'jwt_token') {
        // Impersonated tabs run off sessionStorage — a localStorage token
        // change in another (admin/advisor) tab must not tear down or swap
        // this tab's session.
        if (sessionStorage.getItem("jwt_token")) return
        const newToken = e.newValue
        if (newToken !== currentToken) {
          console.log("AuthContext: Cross-tab token change detected")
          setCurrentToken(newToken)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [currentToken])

  // BroadcastChannel-based cross-tab auth sync. Storage events alone catch
  // jwt_token writes, but a 401 in another tab redirects immediately to
  // /auth before this tab notices — and an explicit logout() in another tab
  // should kick this tab out of any protected page without waiting for the
  // next API call to fail. Broadcasting on logout + 401 (see lib/api.ts)
  // closes that gap.
  useEffect(() => {
    const unsubscribe = subscribeAuth((event) => {
      if (event.type === "logout") {
        // Sync local state and bail out of any protected route.
        setIsLoggedIn(false)
        setUserInfo(null)
        setUserRole(null)
        setCurrentToken(null)
        if (typeof window !== "undefined") {
          const path = window.location.pathname
          const onAuthPage = path === "/auth" || path.startsWith("/auth/")
          if (!onAuthPage) window.location.href = "/auth"
        }
      } else if (event.type === "login" || event.type === "token_changed") {
        // Another tab signed in / refreshed; trust its new token and refetch.
        lastRefreshTimeRef.current = 0
        refreshUserInfo(true)
      }
    })
    return unsubscribe
  }, [refreshUserInfo])

  // Set up event listener for subscription changes
  useEffect(() => {
    const handleSubscriptionChange = () => {
      refreshUserInfo()
    }

    // Listen for the custom event
    window.addEventListener("subscription-changed", handleSubscriptionChange)

    // Check localStorage periodically for changes
    const checkInterval = setInterval(() => {
      const changed = localStorage.getItem("subscription_changed")
      if (changed === "true") {
        localStorage.removeItem("subscription_changed")
        refreshUserInfo()
      }
    }, 5000) // Check less frequently (5 seconds)

    return () => {
      window.removeEventListener("subscription-changed", handleSubscriptionChange)
      clearInterval(checkInterval)
    }
  }, []) // Empty dependency array to avoid cycles

  const handleLogout = () => {
    logout() // This clears localStorage
    
    // Immediately clear all auth state
    setCurrentToken(null)
    setIsLoggedIn(false)
    setUserRole(null)
    setUserInfo(null)
    setIsLoading(false)
    lastRefreshTimeRef.current = 0
  }

  const handleLoginSuccess = useCallback(() => {
    const token = getJWTToken()
    if (token) {
      console.log("AuthContext: onLoginSuccess called, refreshing user info")
      setCurrentToken(token)
      setIsLoggedIn(true)
      setUserInfo(null)
      setUserRole(null)
      lastRefreshTimeRef.current = 0
      refreshUserInfo(true) // Force refresh
    }
  }, [refreshUserInfo])

  const checkAuthStatus = useCallback(() => {
    const status = isAuthenticated()
    setIsLoggedIn((prev) => (prev !== status ? status : prev))
    return status
  }, [])

  const getUserRole = useCallback(async (): Promise<string | null> => {
    if (!isLoggedIn) return null

    // If we already have the user role, return it
    if (userRole) return userRole

    // Otherwise, refresh user info and return the role
    await refreshUserInfo()
    return userRole
  }, [isLoggedIn, userRole, refreshUserInfo])

  // Add isFirstLogin, setIsFirstLogin, and onLoginSuccess to the provider value
  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        userRole,
        userInfo,
        logout: handleLogout,
        checkAuthStatus,
        getUserRole,
        refreshUserInfo,
        onLoginSuccess: handleLoginSuccess,
        isLoading,
        isFirstLogin,
        setIsFirstLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
